/**
 * PATH: packages/job-hub/src/jobs/tabs/job-qa/phone/QaPhoneTitleActions.tsx
 * FILE_ID: JOBHUB-QA-PHONE-TITLE-ACTIONS-001
 * PURPOSE: Home, Select, Add and Templates icons in the QA record title row.
 */
import { createPortal } from 'react-dom'
import { Home, LayoutGrid, Plus, Square, CheckSquare } from 'lucide-react'
import { useHeaderMobileTitleSlot } from '@quotech/ui'
import { QA_PHONE_TITLE_ACTIONS_SLOT_ID } from '../../../qa/phonePage'
import { QA_PHONE_CONTROLS } from './qaPhoneControlInventory'
import { requestQaPhonePage } from './qaPhonePage'
import type { QaPhoneSheetId } from './useQaPhoneUi'

const iconClass = 'flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-muted-foreground hover:text-foreground'

export function QaPhoneTitleActions({
  ui,
  canBuild,
  activeInspectionId,
  allVisibleSelected,
  onToggleSelectAll,
}: {
  ui: {
    selecting: boolean
    setSelecting: (update: (on: boolean) => boolean) => void
    toggleSheet: (id: QaPhoneSheetId) => void
  }
  canBuild: boolean
  activeInspectionId: string | null
  allVisibleSelected: boolean
  onToggleSelectAll: () => void
  inspectionCount: number
}) {
  const slot = useHeaderMobileTitleSlot(QA_PHONE_TITLE_ACTIONS_SLOT_ID)
  if (!slot) return null
  return createPortal(
    <>
      <button
        type="button"
        aria-label="Home"
        title="Start/Add/Edit"
        data-testid={QA_PHONE_CONTROLS.titleHome}
        data-qa-phone-control={QA_PHONE_CONTROLS.titleHome}
        className={iconClass}
        onClick={() => {
          // Reused from: packages/job-hub/src/jobs/tabs/job-qa/phone/qaPhonePage.ts (requestQaPhonePage)
          requestQaPhonePage('setup')
        }}
      >
        <Home className="h-4 w-4" />
      </button>
      <button
        type="button"
        aria-label="Select"
        aria-pressed={allVisibleSelected}
        data-testid={QA_PHONE_CONTROLS.titleSelect}
        data-qa-phone-control={QA_PHONE_CONTROLS.titleSelect}
        className={iconClass}
        onClick={onToggleSelectAll}
      >
        {allVisibleSelected ? <CheckSquare className="h-4 w-4" /> : <Square className="h-4 w-4" />}
      </button>
      {canBuild ? (
        <button
          type="button"
          aria-label="Add"
          data-testid={QA_PHONE_CONTROLS.titleAdd}
          data-qa-phone-control={QA_PHONE_CONTROLS.titleAdd}
          className={iconClass}
          onClick={() => ui.toggleSheet('add')}
        >
          <Plus className="h-4 w-4" />
        </button>
      ) : null}
      {canBuild && activeInspectionId ? (
        <button
          type="button"
          aria-label="Templates"
          data-testid={QA_PHONE_CONTROLS.titleTemplates}
          data-qa-phone-control={QA_PHONE_CONTROLS.titleTemplates}
          className={iconClass}
          onClick={() => ui.toggleSheet('templates')}
        >
          <LayoutGrid className="h-4 w-4" />
        </button>
      ) : null}
    </>,
    slot,
  )
}
